import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { SidePanelProps } from "../../layout/sidePanels";
import { skillsQueryOptions, skillLabel } from "../../queries/skills-queries";
import { sessionMetaQueryOptions } from "../../queries/session-queries";
import { agentsCatalogueQueryOptions } from "../../queries/agents-queries";
import { useAgentDraftStore } from "@/modules/agents/agent-draft-store";

interface SkillsState {
  recent?: string[];
}

/**
 * Skills panel — lists connected AgentSkills and highlights skills the
 * agent has activated in this session.
 *
 * Data sources:
 *   - List:      REST /api/skills (React Query, cached process-wide)
 *   - Highlight: every name that appears in state.skills.recent
 *
 * Backend: `SkillTracker` hook provider in backend/stream.py observes every
 * BeforeToolCallEvent, and when `tool.name === 'skills'` it prepends the
 * called `skill_name` to state.skills.recent (deduped, capped at 10).
 *
 * Highlight model: any skill that's been activated stays lit. The CSS
 * pulse animation runs once when the chip first picks up `--active`
 * (i.e. on the activation frame); after that the chip stays in the
 * "activated" colour for the rest of the session. This matches what the
 * tool-call log says — «Подключен навык: X» means it stays connected.
 *
 * Display names come from `agent/skills/<id>/meta.json::label` when present,
 * falling back to the skill's technical id (e.g. "docx").
 */
export function SkillsPanel({ state, sessionId }: SidePanelProps) {
  const { data: skills, isLoading: skillsLoading } = useQuery(skillsQueryOptions());
  // Use the per-session /meta endpoint instead of finding in the global
  // sessions list — the list filters by status='active', so a freshly
  // created chat (status='draft' until the first turn promotes it)
  // wouldn't be there until generation finished. /meta is unfiltered and
  // exposes the agent summary, so skills resolve from the first paint.
  const { data: meta, isLoading: metaLoading } = useQuery(
    sessionMetaQueryOptions(sessionId ?? null),
  );

  // Hero source — same pattern as HeroConnectorsPill. The pre-session
  // panel must filter against the agent that *will* run, otherwise the
  // chip grid briefly shows the full space catalogue between the hero
  // render and the /meta refetch (the "flicker" on session start).
  const pendingAgentRef = useAgentDraftStore((s) => s.pendingRef);
  const { data: agentsData } = useQuery(agentsCatalogueQueryOptions());
  const heroAgent = useMemo(() => {
    if (sessionId) return null;
    const list = agentsData?.agents ?? [];
    const def = agentsData?.defaultAgent ?? null;
    if (pendingAgentRef) return list.find((a) => a.ref === pendingAgentRef) ?? def;
    return def;
  }, [sessionId, agentsData, pendingAgentRef]);

  // Both queries must resolve before we know which skills the agent
  // has — otherwise the panel either shows nothing useful or, worse,
  // leaks the full cross-space catalogue. Inside a session that's
  // /meta; outside it, the agent catalogue (and only when we don't yet
  // know which agent is selected).
  const isLoading =
    skillsLoading
    || (!!sessionId && (metaLoading || !meta))
    || (!sessionId && !agentsData);

  const skillsState = (state.skills as SkillsState | undefined) ?? {};
  const activatedNames = useMemo(
    () => new Set(skillsState.recent ?? []),
    [skillsState.recent],
  );

  // Restrict the catalogue to the active agent's allowlist. Empty
  // allowlist → empty grid (matches runtime: no AgentSkills plugin is
  // registered). On hero, the allowlist comes from the pending/default
  // agent; in a session, from the snapshot via /meta.
  const allowedSkills = useMemo(() => {
    if (!skills) return skills;
    let refs: string[] | null = null;
    if (sessionId) {
      if (!meta) return undefined; // render «Загрузка…»
      refs = meta.agent?.skill_ids ?? [];
    } else {
      if (!agentsData) return undefined;
      refs = heroAgent?.skill_ids ?? [];
    }
    const allowed = new Set(refs);
    return skills.filter((s) => allowed.has(s.ref));
  }, [skills, meta, sessionId, agentsData, heroAgent]);

  return (
    <details className="details-section" open>
      <summary className="details-section-header">
        <span>Навыки</span>
        {allowedSkills && allowedSkills.length > 0 && (
          <span className="details-count-badge">{allowedSkills.length}</span>
        )}
      </summary>
      <div className="details-section-body">
        {isLoading ? (
          <div className="details-empty">Загрузка…</div>
        ) : !allowedSkills || allowedSkills.length === 0 ? (
          <div className="details-empty">Навыки не подключены</div>
        ) : (
          <div className="skills-grid">
            {allowedSkills.map((skill) => {
              const isActive = activatedNames.has(skill.name);
              const label = skillLabel(skill);
              return (
                <div
                  key={skill.id}
                  className={`skill-chip${isActive ? ' skill-chip--active' : ''}`}
                  title={skill.description}
                >
                  <span className="skill-chip-name">{label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </details>
  );
}
